import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/types/database.types";

// Define KnowledgeItem type if not imported
export type KnowledgeItem = {
  id: string;
  title: string;
  type: string;
  content?: string | null;
  url?: string | null;
  file_path?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  file_type?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  organization_id: string;
  is_active?: boolean | null;
  ai_processed?: boolean | null;
  ai_file_id?: string | null;
  ai_processing_error?: string | null;
  vector_store_id?: string | null;
  vector_store_status?: string | null;
  agent_id?: string | null;
};

// Memoize the Supabase URLs and keys
const getSupabaseUrls = (() => {
  // Cache the computed values
  let supabaseUrl: string | undefined;
  let functionsUrl: string | undefined;
  let anonKey: string | undefined;

  return () => {
    // Return cached values if already computed
    if (supabaseUrl && functionsUrl && anonKey) {
      return { supabaseUrl, functionsUrl, anonKey };
    }

    // Compute values once
    supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    functionsUrl = supabaseUrl?.replace("supabase.co", "functions.supabase.co");
    anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    return { supabaseUrl, functionsUrl, anonKey };
  };
})();

/**
 * Interface for file upload response
 */
export interface FileUploadResponse {
  id: string;
  object: string;
  bytes: number;
  created_at: number;
  filename: string;
  purpose: string;
  status: string;
  status_details: any;
  agent_id?: string;
}

/**
 * Interface for vector store response
 */
export interface VectorStoreResponse {
  id: string;
  object: string;
  created_at: number;
  name: string;
  file_counts: {
    total: number;
    completed: number;
    in_progress: number;
    failed: number;
  };
}

/**
 * Interface for sync results
 */
export interface SyncResults {
  success: boolean;
  totalItems: number;
  successfulItems: number;
  failedItems: number;
  errors: { id: string; error: string }[];
}

/**
 * Uploads a file to OpenAI and gets a file_id
 */
export async function uploadFileToOpenAI(
  file: File,
  purpose: string = "user_data",
  sourceType: string | null = null,
  agentId: string | null = null,
  organizationId: string | null = null
) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    // Use the Edge Function to upload to avoid exposing API keys
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", purpose);

    if (sourceType) {
      formData.append("source_type", sourceType);
    }

    if (agentId) {
      formData.append("agent_id", agentId);
    }

    if (organizationId) {
      formData.append("organization_id", organizationId);
    }

    // Note: We don't send metadata to the Edge Function because
    // OpenAI Files API doesn't support it, but we can still use
    // the metadata for logging purposes

    if (agentId) {
      console.log(`[fileService] Uploading file for agent ${agentId}`);
    }

    console.log(
      `[fileService] Uploading file ${file.name} (${file.size} bytes) ${agentId ? `for agent ${agentId}` : ""}`
    );

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${anonKey}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to upload file");
    }

    const result = await response.json();
    console.log(
      `[fileService] File uploaded successfully, received ID: ${result.id}`
    );

    return { success: true, data: result as FileUploadResponse };
  } catch (error) {
    console.error("[fileService] Error uploading file:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Uploads text content as a file to OpenAI for vector search
 */
export async function uploadTextContent(
  content: string,
  filename: string,
  metadata?: Record<string, any>,
  agentId?: string,
  organizationId?: string
) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    // Convert text content to File object
    const textBlob = new Blob([content], { type: "text/plain" });
    const textFile = new File([textBlob], filename, { type: "text/plain" });

    console.log(
      `[fileService] Uploading text content as file: ${filename} (${textFile.size} bytes) ${agentId ? `for agent ${agentId}` : ""}`
    );

    // Use the Edge Function to upload to avoid exposing API keys
    const formData = new FormData();
    formData.append("file", textFile);
    formData.append("purpose", "user_data");
    formData.append("source_type", "snippet");

    if (agentId) {
      formData.append("agent_id", agentId);
    }

    if (organizationId) {
      formData.append("organization_id", organizationId);
    }

    if (metadata) {
      console.log(`[fileService] Text content metadata:`, metadata);
    }

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${anonKey}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to upload text content");
    }

    const result = await response.json();
    console.log(
      `[fileService] Text content uploaded successfully, received ID: ${result.id}`
    );

    return { success: true, data: result as FileUploadResponse };
  } catch (error) {
    console.error("[fileService] Error uploading text content:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Processes a link by crawling its content and adding it to the vector store
 */
export async function processLinkForAI(
  url: string,
  linkId: string,
  title: string,
  description: string | null,
  organizationId: string,
  agentId?: string | null,
  onProgress?: (stage: 'INITIATING' | 'CRAWLING' | 'PROCESSING' | 'FINALIZING' | 'COMPLETED') => void,
  pages: number = 1,
  isRecrawl: boolean = false
) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(
      `[fileService] ${isRecrawl ? 'Recrawling' : 'Processing'} link for AI: ${url} (${linkId}) ${agentId ? `for agent ${agentId}` : "for organization"} - ${pages} pages`
    );

    // Stage 1: Initiating
    onProgress?.('INITIATING');

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "processLinkForAI",
        url,
        linkId,
        title,
        description,
        organizationId,
        agentId,
        pages,
        isRecrawl
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[fileService] Edge function error:`, errorText);
      throw new Error(`Failed to process link: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || "Failed to process link");
    }

    // Final stage
    onProgress?.('COMPLETED');

    console.log(`[fileService] Successfully ${isRecrawl ? 'recrawled' : 'processed'} link:`, result);
    return result;

  } catch (error) {
    console.error(`[fileService] Error ${isRecrawl ? 'recrawling' : 'processing'} link:`, error);
    
    // Validate response structure before returning
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    
    return {
      success: false,
      error: errorMessage
    };
  }
}

/**
 * Processes any knowledge item (file/snippet/link with content) for AI by uploading to OpenAI and vector store
 */
export async function processKnowledgeItem(
  knowledgeItemId: string,
  organizationId: string,
  onProgress?: (stage: 'PROCESSING' | 'FINALIZING' | 'COMPLETED' | 'ERROR') => void
) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(
      `[fileService] Processing knowledge item for AI: ${knowledgeItemId} (organization: ${organizationId})`
    );

    // Stage 1: Processing
    onProgress?.('PROCESSING');

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "processKnowledgeItem",
        knowledgeItemId,
        organizationId
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[fileService] Edge function error:`, errorText);
      throw new Error(`Failed to process knowledge item: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      onProgress?.('ERROR');
      throw new Error(result.error || "Failed to process knowledge item");
    }

    // Final stage
    onProgress?.('COMPLETED');

    console.log(`[fileService] Successfully processed knowledge item:`, result);
    return result;

  } catch (error) {
    console.error(`[fileService] Error processing knowledge item:`, error);
    onProgress?.('ERROR');
    
    // Validate response structure before returning
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    
    return {
      success: false,
      error: errorMessage
    };
  }
}

/**
 * Creates a vector store or gets an existing one
 */
export async function getOrCreateVectorStore(
  name: string,
  organizationId: string
) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    // Create a name that includes the organization ID to ensure uniqueness
    const fullName = `org-files-${organizationId}`;

    console.log(`[fileService] Getting or creating vector store: ${fullName}`);

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "createVectorStore",
        name: fullName,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create vector store");
    }

    const result = await response.json();
    console.log(
      `[fileService] Vector store operation successful, ID: ${result.id}`
    );

    // Also save the mapping in our database for future reference
    // TODO: Fix database constraints before re-enabling this
    // Check if mapping already exists first
    // const { data: existingMapping } = await supabase
    //   .from("vector_store_mappings")
    //   .select("id")
    //   .eq("organization_id", organizationId)
    //   .is("agent_id", null)
    //   .single();

    // if (!existingMapping) {
    //   await supabase.from("vector_store_mappings").insert({
    //     organization_id: organizationId,
    //     agent_id: null,
    //     vector_store_id: result.id,
    //     created_at: new Date().toISOString(),
    //   });
    // }

    return { success: true, data: result as VectorStoreResponse };
  } catch (error) {
    console.error("[fileService] Error with vector store operation:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Creates or gets an agent-specific vector store
 */
export async function getOrCreateAgentVectorStore(
  agentId: string,
  organizationId: string
) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    // Create a name using the agent-specific naming convention
    const fullName = `agent-files-${agentId}`;

    console.log(
      `[fileService] Getting or creating agent vector store: ${fullName}`
    );

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "createAgentVectorStore",
        agentId,
        organizationId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create agent vector store");
    }

    const result = await response.json();
    console.log(
      `[fileService] Agent vector store operation successful, ID: ${result.id}`
    );

    // Also save the mapping in our database for future reference
    // TODO: Fix database constraints before re-enabling this
    // Check if mapping already exists first
    // const { data: existingMapping } = await supabase
    //   .from("vector_store_mappings")
    //   .select("id")
    //   .eq("organization_id", organizationId)
    //   .eq("agent_id", agentId)
    //   .single();

    // if (!existingMapping) {
    //   await supabase.from("vector_store_mappings").insert({
    //     organization_id: organizationId,
    //     agent_id: agentId,
    //     vector_store_id: result.id,
    //     created_at: new Date().toISOString(),
    //   });
    // }

    return { success: true, data: result as VectorStoreResponse };
  } catch (error) {
    console.error(
      "[fileService] Error with agent vector store operation:",
      error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Get the vector store ID for a specific agent
 */
export async function getVectorStoreIdForAgent(agentId: string) {
  try {
    // First check if we have this mapping in our database
    const { data, error } = await supabase
      .from("vector_store_mappings")
      .select("vector_store_id")
      .eq("agent_id", agentId)
      .single();

    if (error) {
      throw error;
    }

    if (data && data.vector_store_id) {
      return { success: true, data: { id: data.vector_store_id } };
    }

    return { success: false, error: "Vector store not found for agent" };
  } catch (error) {
    console.error(
      "[fileService] Error getting vector store ID for agent:",
      error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Transfer a file from one agent to another
 */
export async function transferFile(
  fileId: string,
  targetAgentId: string,
  sourceAgentId: string | null,
  organizationId: string
) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(
      `[fileService] Transferring file ${fileId} from ${sourceAgentId || "organization"} to agent ${targetAgentId}`
    );

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "transferFileToAgent",
        file_id: fileId,
        target_agent_id: targetAgentId,
        source_agent_id: sourceAgentId,
        organization_id: organizationId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to transfer file");
    }

    const result = await response.json();

    // Also update the knowledge_items table to reflect the transfer
    if (result.success) {
      await supabase
        .from("knowledge_items")
        .update({ agent_id: targetAgentId })
        .eq("ai_file_id", fileId);
    }

    console.log(`[fileService] File transfer successful`);
    return { success: true, data: result };
  } catch (error) {
    console.error("[fileService] Error transferring file:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * List files for a specific agent
 */
export async function listAgentFiles(agentId: string, organizationId: string) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(`[fileService] Listing files for agent ${agentId}`);

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "listAgentFiles",
        agent_id: agentId,
        organization_id: organizationId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to list agent files");
    }

    const result = await response.json();
    console.log(
      `[fileService] Successfully listed ${result.data.length} files for agent`
    );
    return { success: true, data: result.data };
  } catch (error) {
    console.error("[fileService] Error listing agent files:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Attaches a file to a vector store
 */
export async function attachFileToVectorStore(
  vectorStoreId: string,
  fileId: string
) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(
      `[fileService] Attaching file ${fileId} to vector store ${vectorStoreId}`
    );

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "attachFile",
        vectorStoreId,
        fileId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to attach file to vector store"
      );
    }

    const result = await response.json();
    console.log(`[fileService] File attached successfully to vector store`);

    return { success: true, data: result };
  } catch (error) {
    console.error("[fileService] Error attaching file to vector store:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Gets the vector store ID for an organization
 */
export async function getVectorStoreIdForOrganization(organizationId: string) {
  try {
    const { data, error } = await supabase
      .from("vector_store_mappings")
      .select("vector_store_id")
      .eq("organization_id", organizationId)
      .is("agent_id", null)
      .single();

    if (error) {
      console.error("[fileService] Error fetching vector store ID:", error);
      return { success: false, error: error.message };
    }

    if (!data) {
      return {
        success: false,
        error: "No vector store found for this organization",
      };
    }

    if (!data.vector_store_id) {
      return {
        success: false,
        error: "Vector store ID is missing",
      };
    }

    return { success: true, vectorStoreId: data.vector_store_id };
  } catch (error) {
    console.error("[fileService] Error getting vector store ID:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Deletes a file from OpenAI
 */
export async function deleteFile(fileId: string) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(`[fileService] === FILE DELETION REQUEST FROM CLIENT ===`);
    console.log(`[fileService] File ID to delete: "${fileId}"`);
    console.log(`[fileService] File ID type: ${typeof fileId}`);
    console.log(`[fileService] File ID length: ${fileId?.length || 0}`);

    if (!fileId || fileId.trim() === "") {
      console.error("[fileService] Attempted to delete file with empty ID");
      return {
        success: false,
        error: "File ID is required and cannot be empty",
      };
    }

    // Check if fileId has the expected format (typically starts with "file-")
    if (!fileId.startsWith("file-")) {
      console.warn(
        `[fileService] Warning: File ID "${fileId}" doesn't start with "file-" prefix, which is unusual for OpenAI file IDs`
      );
    }

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "deleteFile",
        fileId,
      }),
    });

    console.log(
      `[fileService] Delete request sent, response status: ${response.status}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error(
        `[fileService] Error response from file-service:`,
        errorData
      );
      throw new Error(errorData.error || "Failed to delete file");
    }

    const data = await response.json();
    console.log(`[fileService] Delete operation successful:`, data);

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("[fileService] Error deleting file:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Lists all files from OpenAI
 * @param purpose Optional filter by purpose (e.g. "assistants")
 */
export async function listFiles(purpose?: string) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(
      `[fileService] Listing files from OpenAI${purpose ? ` with purpose ${purpose}` : ""}`
    );

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "listFiles",
        purpose,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to list files");
    }

    const data = await response.json();

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("[fileService] Error listing files:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Lists all files attached to a vector store
 * @param vectorStoreId The ID of the vector store
 */
export async function listFilesInVectorStore(vectorStoreId: string) {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(`[fileService] Listing files in vector store ${vectorStoreId}`);

    const response = await fetch(`${functionsUrl}/file-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        action: "listFilesInVectorStore",
        vectorStoreId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to list files in vector store"
      );
    }

    const data = await response.json();

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("[fileService] Error listing files in vector store:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Syncs a single snippet to OpenAI's vector store
 */
export async function syncSnippet(
  snippet: KnowledgeItem,
  organizationId: string
) {
  try {
    if (snippet.type !== "snippet" || !snippet.content) {
      return {
        success: false,
        error: "Invalid snippet or missing content",
      };
    }

    // 1. Create a filename for the snippet
    const filename = `snippet_${snippet.id}_${Date.now()}.txt`;

    // 2. Prepare metadata
    const metadata = {
      title: snippet.title,
      organization_id: organizationId,
      item_id: snippet.id,
      type: "snippet",
    };

    // 3. Upload text content as a file to OpenAI
    const uploadResult = await uploadTextContent(
      snippet.content,
      filename,
      metadata
    );

    if (!uploadResult.success) {
      throw new Error(uploadResult.error as string);
    }

    // 4. Get or create vector store for this organization
    const vectorStoreResult = await getOrCreateVectorStore(
      "knowledge",
      organizationId
    );

    if (!vectorStoreResult.success) {
      throw new Error(vectorStoreResult.error as string);
    }

    // 5. Attach snippet file to vector store
    const attachResult = await attachFileToVectorStore(
      vectorStoreResult.data.id,
      uploadResult.data.id
    );

    if (!attachResult.success) {
      throw new Error(attachResult.error as string);
    }

    // 6. Update the snippet with AI processing metadata
    const { error: updateError } = await supabase
      .from("knowledge_items")
      .update({
        ai_processed: true,
        ai_file_id: uploadResult.data.id,
        updated_at: new Date().toISOString(),
      })
      .eq("id", snippet.id);

    if (updateError) {
      console.error(
        "[fileService] Error updating snippet AI status:",
        updateError
      );
      // Continue anyway since the file is already attached
    }

    return {
      success: true,
      snippet_id: snippet.id,
      file_id: uploadResult.data.id,
      vector_store_id: vectorStoreResult.data.id,
    };
  } catch (error) {
    console.error(`[fileService] Error syncing snippet ${snippet.id}:`, error);
    return {
      success: false,
      snippet_id: snippet.id,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Syncs multiple snippets to OpenAI's vector store
 */
export async function syncSnippets(
  snippets: KnowledgeItem[],
  organizationId: string
): Promise<SyncResults> {
  const results: SyncResults = {
    success: false,
    totalItems: snippets.length,
    successfulItems: 0,
    failedItems: 0,
    errors: [],
  };

  if (snippets.length === 0) {
    results.success = true;
    return results;
  }

  // Get or create vector store once for all snippets
  const vectorStoreResult = await getOrCreateVectorStore(
    "knowledge",
    organizationId
  );

  if (!vectorStoreResult.success) {
    results.errors.push({
      id: "vector-store",
      error: vectorStoreResult.error as string,
    });
    return results;
  }

  const vectorStoreId = vectorStoreResult.data.id;

  // Process each snippet
  for (const snippet of snippets) {
    try {
      if (snippet.type !== "snippet" || !snippet.content) {
        results.failedItems++;
        results.errors.push({
          id: snippet.id,
          error: "Invalid snippet or missing content",
        });
        continue;
      }

      // 1. Create a filename for the snippet
      const filename = `snippet_${snippet.id}_${Date.now()}.txt`;

      // 2. Upload text content as a file to OpenAI
      const uploadResult = await uploadTextContent(snippet.content, filename, {
        title: snippet.title,
        organization_id: organizationId,
        item_id: snippet.id,
      });

      if (!uploadResult.success) {
        results.failedItems++;
        results.errors.push({
          id: snippet.id,
          error: uploadResult.error as string,
        });
        continue;
      }

      // 3. Attach snippet file to vector store
      const attachResult = await attachFileToVectorStore(
        vectorStoreId,
        uploadResult.data.id
      );

      if (!attachResult.success) {
        results.failedItems++;
        results.errors.push({
          id: snippet.id,
          error: attachResult.error as string,
        });
        continue;
      }

      // 4. Update the snippet with AI processing metadata
      const { error: updateError } = await supabase
        .from("knowledge_items")
        .update({
          ai_processed: true,
          ai_file_id: uploadResult.data.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", snippet.id);

      if (updateError) {
        console.error(
          `[fileService] Error updating snippet ${snippet.id} AI status:`,
          updateError
        );
        // Continue anyway since the file is already attached
      }

      results.successfulItems++;
    } catch (error) {
      console.error(
        `[fileService] Error syncing snippet ${snippet.id}:`,
        error
      );
      results.failedItems++;
      results.errors.push({
        id: snippet.id,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  results.success = results.failedItems === 0;

  return results;
}

/**
 * Updates all snippets for an organization as a single consolidated JSON file
 * This replaces the previous approach of uploading each snippet individually
 */
export async function updateOrganizationSnippets(
  organizationId: string,
  deleteExistingFiles: boolean = true
): Promise<{
  success: boolean;
  error?: string;
  data?: {
    fileId: string;
    snippetCount: number;
  };
}> {
  try {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    console.log(
      `[fileService] Updating snippets for organization ${organizationId}`
    );

    // 1. Fetch all snippets for this organization
    const { data: snippets, error: fetchError } = await supabase
      .from("knowledge_items")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("type", "snippet");

    if (fetchError) {
      throw new Error(`Failed to fetch snippets: ${fetchError.message}`);
    }

    if (!snippets || snippets.length === 0) {
      console.log(
        `[fileService] No snippets found for organization ${organizationId}`
      );
      return {
        success: true,
        data: {
          fileId: "",
          snippetCount: 0,
        },
      };
    }

    console.log(
      `[fileService] Found ${snippets.length} snippets for organization ${organizationId}`
    );

    // 2. If requested, delete any existing snippet files
    if (deleteExistingFiles) {
      const fileIds = snippets
        .filter((snippet) => (snippet as any).ai_file_id)
        .map((snippet) => (snippet as any).ai_file_id);

      console.log(
        `[fileService] Deleting ${fileIds.length} existing snippet files`
      );

      for (const fileId of fileIds) {
        if (fileId) {
          await deleteFile(fileId);
        }
      }
    }

    // 3. Create a single JSON file with all snippets
    const snippetsData = snippets.map((snippet) => ({
      id: snippet.id,
      title: snippet.title || "Untitled",
      content: snippet.content || "",
      updated_at: snippet.updated_at,
      created_at: snippet.created_at,
    }));

    const jsonContent = JSON.stringify(
      {
        organization_id: organizationId,
        snippet_count: snippets.length,
        updated_at: new Date().toISOString(),
        snippets: snippetsData,
      },
      null,
      2
    );

    // 4. Upload the JSON file
    const filename = `org_${organizationId}_snippets_${Date.now()}.json`;
    const uploadResult = await uploadTextContent(jsonContent, filename, {
      organization_id: organizationId,
      type: "organization_snippets",
      snippet_count: String(snippets.length),
    });

    if (!uploadResult.success) {
      throw new Error(
        `Failed to upload organization snippets: ${uploadResult.error}`
      );
    }

    const fileId = uploadResult.data.id;
    console.log(
      `[fileService] Uploaded organization snippets as ${filename}, file ID: ${fileId}`
    );

    // 5. Get or create vector store for this organization
    const vectorStoreResult = await getOrCreateVectorStore(
      "knowledge",
      organizationId
    );

    if (!vectorStoreResult.success) {
      throw new Error(`Failed to get vector store: ${vectorStoreResult.error}`);
    }

    // 6. Attach the file to the vector store
    const attachResult = await attachFileToVectorStore(
      vectorStoreResult.data.id,
      fileId
    );

    if (!attachResult.success) {
      throw new Error(
        `Failed to attach file to vector store: ${attachResult.error}`
      );
    }

    // 7. Update all snippets' AI status
    const { error: updateError } = await supabase
      .from("knowledge_items")
      .update({
        ai_processed: true,
        ai_file_id: fileId,
        updated_at: new Date().toISOString(),
      })
      .eq("organization_id", organizationId)
      .eq("type", "snippet");

    if (updateError) {
      console.error(
        `[fileService] Error updating snippets' AI status: ${updateError.message}`
      );
      // Continue anyway since the file is already attached
    }

    return {
      success: true,
      data: {
        fileId,
        snippetCount: snippets.length,
      },
    };
  } catch (error) {
    console.error("[fileService] Error updating organization snippets:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Utility function to check and diagnose vector store file issues
 * This helps track down problems with file IDs and vector stores
 */
export async function checkFilesInVectorStore(organizationId: string) {
  try {
    // Step 1: Get vector store ID for this organization
    console.log(`[fileService] === VECTOR STORE DIAGNOSTIC ===`);
    console.log(
      `[fileService] Checking vector store for organization: ${organizationId}`
    );

    const vectorStoreResult =
      await getVectorStoreIdForOrganization(organizationId);

    if (!vectorStoreResult.success) {
      console.error(
        `[fileService] No vector store found for organization ${organizationId}:`,
        vectorStoreResult.error
      );
      return {
        success: false,
        error: vectorStoreResult.error,
        diagnostic: {
          organizationId,
          vectorStoreFound: false,
        },
      };
    }

    const vectorStoreId = vectorStoreResult.vectorStoreId;
    console.log(`[fileService] Found vector store ID: ${vectorStoreId}`);

    // Step 2: Get all snippets for this organization from the database
    const { data: snippets, error: snippetsError } = await supabase
      .from("knowledge_items")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("type", "snippet");

    if (snippetsError) {
      console.error(`[fileService] Error fetching snippets:`, snippetsError);
      return {
        success: false,
        error: snippetsError.message,
        diagnostic: {
          organizationId,
          vectorStoreId,
          vectorStoreFound: true,
          snippetsError: snippetsError.message,
        },
      };
    }

    // Extract file IDs from snippets
    const snippetFileIds = snippets
      .filter((snippet) => (snippet as any).ai_file_id)
      .map((snippet) => ({
        snippetId: snippet.id,
        fileId: (snippet as any).ai_file_id,
        title: snippet.title,
      }));

    console.log(
      `[fileService] Found ${snippetFileIds.length} snippets with fileIds in database`
    );
    if (snippetFileIds.length > 0) {
      console.log(
        `[fileService] Sample file IDs from database:`,
        snippetFileIds.slice(0, 3).map((s) => `${s.fileId} (${s.title})`)
      );
    }

    // Step 3: List all files in OpenAI
    const allFilesResult = await listFiles();

    if (!allFilesResult.success) {
      console.error(`[fileService] Error listing files:`, allFilesResult.error);
      return {
        success: false,
        error: allFilesResult.error,
        diagnostic: {
          organizationId,
          vectorStoreId,
          vectorStoreFound: true,
          snippetsWithFileIds: snippetFileIds.length,
          allFilesError: allFilesResult.error,
        },
      };
    }

    const allFiles = allFilesResult.data.data || [];
    console.log(
      `[fileService] Found ${allFiles.length} total files in OpenAI account`
    );

    // Step 4: List files in vector store
    const vectorStoreFilesResult = await listFilesInVectorStore(vectorStoreId);

    if (!vectorStoreFilesResult.success) {
      console.error(
        `[fileService] Error listing files in vector store:`,
        vectorStoreFilesResult.error
      );
      return {
        success: false,
        error: vectorStoreFilesResult.error,
        diagnostic: {
          organizationId,
          vectorStoreId,
          vectorStoreFound: true,
          snippetsWithFileIds: snippetFileIds.length,
          totalFiles: allFiles.length,
          vectorStoreFilesError: vectorStoreFilesResult.error,
        },
      };
    }

    const vectorStoreFiles = vectorStoreFilesResult.data.data || [];
    console.log(
      `[fileService] Found ${vectorStoreFiles.length} files in vector store ${vectorStoreId}`
    );

    // Step 5: Cross-check file IDs
    const fileIdsInOpenAI = new Set(allFiles.map((file) => file.id));
    const fileIdsInVectorStore = new Set(
      vectorStoreFiles.map((file) => file.file_id)
    );

    // Check which snippet file IDs actually exist in OpenAI
    const missingFileIds = snippetFileIds.filter(
      (item) => !fileIdsInOpenAI.has(item.fileId)
    );
    console.log(
      `[fileService] ${missingFileIds.length} of ${snippetFileIds.length} snippet file IDs are missing from OpenAI`
    );

    if (missingFileIds.length > 0) {
      console.log(
        `[fileService] Missing file IDs:`,
        missingFileIds
          .slice(0, 5)
          .map((s) => `${s.fileId} (snippet: ${s.snippetId})`)
      );
    }

    // Check which snippet file IDs are not in the vector store
    const fileIdsNotInVectorStore = snippetFileIds.filter(
      (item) => !fileIdsInVectorStore.has(item.fileId)
    );
    console.log(
      `[fileService] ${fileIdsNotInVectorStore.length} of ${snippetFileIds.length} snippet file IDs are not in the vector store`
    );

    return {
      success: true,
      diagnostic: {
        organizationId,
        vectorStoreId,
        vectorStoreFound: true,
        snippetsCount: snippets.length,
        snippetsWithFileIds: snippetFileIds.length,
        totalFilesInOpenAI: allFiles.length,
        filesInVectorStore: vectorStoreFiles.length,
        missingFileIdsCount: missingFileIds.length,
        missingFileIds: missingFileIds.slice(0, 10).map((s) => s.fileId),
        fileIdsNotInVectorStoreCount: fileIdsNotInVectorStore.length,
        fileIdsNotInVectorStore: fileIdsNotInVectorStore
          .slice(0, 10)
          .map((s) => s.fileId),
      },
    };
  } catch (error) {
    console.error("[fileService] Error in checkFilesInVectorStore:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      diagnostic: {
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }
}

export default {
  uploadFileToOpenAI,
  uploadTextContent,
  getOrCreateVectorStore,
  attachFileToVectorStore,
  getVectorStoreIdForOrganization,
  deleteFile,
  syncSnippet,
  syncSnippets,
  updateOrganizationSnippets,
  listFiles,
  listFilesInVectorStore,
  checkFilesInVectorStore,
  getOrCreateAgentVectorStore,
  getVectorStoreIdForAgent,
  transferFile,
  listAgentFiles,
  processKnowledgeItem,
};
